import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {Component<T>}
 */
export class ProductCardLink extends Component {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest(
        'a[href], button, input, label, select, [tabindex="1"]'
      );
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false'
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

/**
 * A custom element that displays a product card.
 * Extends ProductCardLink to inherit view transition functionality.
 *
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    this.#randomizeProductCardSwatchOrder();
    this.#refreshAllSwatchPreviewAttributes();

    this.#markSwatchesLayoutReady();

    requestAnimationFrame(() => {
      this.#updateVariantImages();
    });

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }

    this.#refreshAllSwatchPreviewAttributes();

    requestAnimationFrame(() => {
      this.#updateVariantImages();
    });
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages(event);
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const responseProductCard = event.detail.data.html?.querySelector('product-card');
    const anchorElement = responseProductCard?.querySelector('a');
    const featuredMediaUrl = responseProductCard?.getAttribute('data-featured-media-url');

    // Update the featured media URL for view transitions (inherited from ProductCardLink)
    if (featuredMediaUrl) {
      this.setAttribute('data-featured-media-url', featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
      const customizeLink = this.querySelector('a.quick-add__button--choose[href]');
      if (customizeLink instanceof HTMLAnchorElement) {
        customizeLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Fisher–Yates shuffle (mutates array).
   * @param {unknown[]} items
   */
  #shuffleArray(items) {
    for (let i = items.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [items[i], items[j]] = [items[j], items[i]];
    }
  }

  #markSwatchesLayoutReady() {
    if (!this.querySelector('variant-picker[data-product-card-swatch-limit]')) return;

    this.dataset.swatchesLayoutReady = 'true';
    this.dispatchEvent(new CustomEvent('product-card:swatches-layout-ready', { bubbles: true }));
  }

  /**
   * Product cards: pick a random visible swatch on each load, show it first, shuffle the rest.
   */
  #randomizeProductCardSwatchOrder() {
    const pickers = this.querySelectorAll('variant-picker[data-product-card-swatch-limit]');

    for (const picker of pickers) {
      if (!(picker instanceof HTMLElement)) continue;

      const fieldsets = picker.querySelectorAll('fieldset.variant-option--swatches');

      for (const fieldset of fieldsets) {
        const labels = Array.from(
          fieldset.querySelectorAll('label.variant-option__button-label--has-swatch')
        );

        if (!labels.length) continue;

        const inputs = labels
          .map((label) => label.querySelector('input[type="radio"]'))
          .filter((input) => input instanceof HTMLInputElement);

        if (!inputs.length) continue;

        for (const input of inputs) {
          input.checked = false;
          input.dataset.currentChecked = 'false';
        }

        const selectedInput = inputs[Math.floor(Math.random() * inputs.length)];
        if (!(selectedInput instanceof HTMLInputElement)) continue;

        selectedInput.checked = true;
        selectedInput.dataset.currentChecked = 'true';

        const selectedLabel = selectedInput.closest('label');
        const others = labels.filter((label) => label !== selectedLabel);

        this.#shuffleArray(others);

        if (selectedLabel instanceof HTMLElement) {
          selectedLabel.style.order = '0';
        }

        others.forEach((label, index) => {
          if (label instanceof HTMLElement) {
            label.style.order = String(index + 1);
          }
        });

        if (picker instanceof HTMLElement && 'updateSelectedOption' in picker) {
          /** @type {import('@theme/variant-picker').default} */ (picker).updateSelectedOption(selectedInput);
        }

        this.#syncProductCardLinkToVariant(selectedInput);
        this.#refreshSwatchPreviewAttributes(picker);
      }
    }
  }

  /**
   * Updates product card links with the selected variant (no page navigation).
   * @param {HTMLInputElement} input
   */
  #syncProductCardLinkToVariant(input) {
    const variantId = input.dataset.variantId;
    if (!variantId) return;

    const productPath = input.dataset.connectedProductUrl || this.refs.productCardLink?.href || '';
    const pathOnly = productPath.split('?')[0];
    if (!pathOnly) return;

    const url = new URL(pathOnly, window.location.origin);
    url.searchParams.set('variant', variantId);

    const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;
    if (productCardLink instanceof HTMLAnchorElement) {
      productCardLink.href = url.toString();
    }
    if (cardGalleryLink instanceof HTMLAnchorElement) {
      cardGalleryLink.href = url.toString();
    }
    if (productTitleLink instanceof HTMLAnchorElement) {
      productTitleLink.href = url.toString();
    }
    const customizeLink = this.querySelector('a.quick-add__button--choose[href]');
    if (customizeLink instanceof HTMLAnchorElement) {
      customizeLink.href = url.toString();
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   * @param {VariantUpdateEvent} [event]
   */
  #updateVariantImages(event) {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      // Fall back to event variant media if picker hasn't settled yet
      const fallbackId = event?.detail?.resource?.featured_media?.id;
      if (slideshow && fallbackId) {
        slideshow.select({ id: fallbackId }, undefined, { animate: false });
      }
      return;
    }

    const selected = this.variantPicker?.selectedOption;
    const selectedMedia =
      selected instanceof HTMLInputElement ? this.#resolveSwatchPreviewMedia(selected) : null;
    const selectedImageId =
      selectedMedia?.id ||
      selected?.dataset.optionMediaId ||
      event?.detail?.resource?.featured_media?.id;
    const selectedImageUrl = selectedMedia?.url || selected?.closest('label')?.dataset?.optionMediaUrl;

    if (slideshow && selectedImageId) {
      const slide = slideshow.refs.slides?.find(
        (s) => s instanceof HTMLElement && String(s.getAttribute('slide-id')) === String(selectedImageId)
      );

      if (slide) {
        const { slides = [] } = slideshow.refs;

        for (const s of slides) {
          if (s.getAttribute('variant-image') == null) continue;
          s.hidden = s.getAttribute('slide-id') !== selectedImageId;
          // Keep aria-hidden in step: select() below returns early when this slide is already the
          // only visible one, which left the shown photo aria-hidden="true" (Davenport, 2026-10-12).
          s.setAttribute('aria-hidden', s.hidden ? 'true' : 'false');
        }

        slideshow.select({ id: selectedImageId }, undefined, { animate: false });
        return;
      }
    }

    if (selectedImageUrl) {
      this.#setGalleryImageUrl(selectedImageUrl);
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return (
      this.querySelector('swatches-variant-picker-component') ?? this.querySelector('variant-picker')
    );
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /** @type {{ src: string; srcset: string } | null} */
  #galleryImageBackup = null;

  /**
   * @returns {HTMLImageElement | null}
   */
  #getVisibleGalleryImage() {
    const { slideshow } = this.refs;

    if (slideshow?.slides?.length) {
      const currentSlide = slideshow.slides[slideshow.current];
      const currentImg = currentSlide?.querySelector('img');
      if (currentImg instanceof HTMLImageElement) return currentImg;
    }

    const slides = slideshow?.refs?.slides ?? [];

    for (const slide of slides) {
      if (!(slide instanceof HTMLElement)) continue;
      if (slide.hasAttribute('hidden') && !slide.hasAttribute('reveal')) continue;

      const img = slide.querySelector('img');
      if (img instanceof HTMLImageElement) return img;
    }

    const fallback = this.querySelector('.card-gallery img');
    return fallback instanceof HTMLImageElement ? fallback : null;
  }

  /**
   * @typedef {{ id: number, o: string[], m: number, u: string }} VariantPreviewEntry
   */

  /**
   * @param {HTMLElement} picker
   * @returns {VariantPreviewEntry[] | null}
   */
  #parseVariantPreviews(picker) {
    const script = picker.querySelector('script[data-variant-previews]');
    if (!script?.textContent?.trim()) return null;

    try {
      const parsed = JSON.parse(script.textContent);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  /**
   * Resolves featured media for a swatch hover/selection using full variant option data.
   * @param {HTMLInputElement} hoveredInput
   * @returns {{ id?: string; url?: string }}
   */
  #resolveSwatchPreviewMedia(hoveredInput) {
    const label = hoveredInput.closest('label');
    const fallback = {
      id: label?.dataset.optionMediaId,
      url: label?.dataset.optionMediaUrl,
    };

    const picker = hoveredInput.closest('variant-picker, swatches-variant-picker-component');
    if (!(picker instanceof HTMLElement)) return fallback;

    const variants = this.#parseVariantPreviews(picker);
    if (!variants?.length) return fallback;

    const inputId = hoveredInput.dataset.inputId ?? '';
    const hoveredPosition = Number.parseInt(inputId.split('-')[0] ?? '', 10) - 1;
    const hoveredValue = hoveredInput.dataset.optionValueName || hoveredInput.value;

    const checkedInput = picker.querySelector(
      'fieldset.variant-option--swatches input[type="radio"]:checked, input[type="radio"]:checked'
    );
    const checkedId =
      checkedInput instanceof HTMLInputElement ? checkedInput.dataset.variantId : undefined;
    const checkedVariant = variants.find((variant) => String(variant.id) === String(checkedId));

    const match =
      variants.find((variant) => {
        if (hoveredPosition >= 0 && variant.o[hoveredPosition] !== hoveredValue) return false;
        if (!checkedVariant) return true;
        return variant.o.every((optionValue, index) => {
          if (index === hoveredPosition) return true;
          return optionValue === checkedVariant.o[index];
        });
      }) ??
      variants.find((variant) => {
        return hoveredPosition < 0 || variant.o[hoveredPosition] === hoveredValue;
      });

    if (!match) return fallback;

    return {
      id: String(match.m),
      url: match.u,
    };
  }

  #refreshAllSwatchPreviewAttributes() {
    this.querySelectorAll(
      'variant-picker[data-product-card-swatch-limit], swatches-variant-picker-component'
    ).forEach((picker) => {
      if (picker instanceof HTMLElement) {
        this.#refreshSwatchPreviewAttributes(picker);
      }
    });
  }

  /**
   * Keeps swatch label preview attributes aligned with the checked variant context.
   * @param {HTMLElement} picker
   */
  #refreshSwatchPreviewAttributes(picker) {
    const inputs = picker.querySelectorAll('fieldset.variant-option--swatches input[type="radio"]');

    inputs.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;

      const label = input.closest('label');
      if (!(label instanceof HTMLElement)) return;

      const media = this.#resolveSwatchPreviewMedia(input);

      if (media.url) {
        label.dataset.optionMediaUrl = media.url;
      }

      if (media.id) {
        label.dataset.optionMediaId = media.id;
        label.dataset.mediaId = media.id;
        input.dataset.optionMediaId = media.id;
      }
    });
  }

  /**
   * @param {{ id?: string; url?: string }} media
   */
  #previewSwatchMedia(media) {
    const { slideshow } = this.refs;
    const isProductCard = Boolean(
      this.querySelector('variant-picker[data-product-card-swatch-limit]') ||
        this.querySelector('swatches-variant-picker-component script[data-variant-previews]')
    );

    this.resetVariant.cancel();

    const { id, url } = media;

    if (isProductCard) {
      if (url) {
        this.#previewGalleryImageUrl(url);
        return;
      }
    }

    if (!slideshow) {
      if (url) this.#previewGalleryImageUrl(url);
      return;
    }

    if (id) {
      const slide = slideshow.refs.slides?.find(
        (s) => s instanceof HTMLElement && String(s.getAttribute('slide-id')) === String(id)
      );

      if (slide) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    if (url) {
      this.#previewGalleryImageUrl(url);
    }
  }

  /**
   * @param {string} url
   * @param {{ saveBackup?: boolean }} [options]
   */
  #setGalleryImageUrl(url, { saveBackup = false } = {}) {
    const img = this.#getVisibleGalleryImage();
    if (!(img instanceof HTMLImageElement) || !url) return;

    if (saveBackup && !this.#galleryImageBackup) {
      this.#galleryImageBackup = {
        src: img.currentSrc || img.src,
        srcset: img.srcset,
      };
    }

    img.srcset = '';
    img.src = url;
  }

  /**
   * @param {string} url
   */
  #previewGalleryImageUrl(url) {
    this.#setGalleryImageUrl(url, { saveBackup: true });
  }

  /**
   * Hover preview for swatches (slideshow slide or image URL fallback).
   * @param {Event} event
   */
  previewSwatchHover(event) {
    const label = event.target instanceof Element ? event.target.closest('label') : null;
    if (!(label instanceof HTMLElement)) return;

    const input = label.querySelector('input[type="radio"]');
    if (!(input instanceof HTMLInputElement)) return;

    this.#previewSwatchMedia(this.#resolveSwatchPreviewMedia(input));
  }

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    this.#previewSwatchMedia({ id: String(id) });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (this.#galleryImageBackup) {
      const img = this.#getVisibleGalleryImage();
      if (img instanceof HTMLImageElement) {
        img.src = this.#galleryImageBackup.src;
        img.srcset = this.#galleryImageBackup.srcset;
      }
      this.#galleryImageBackup = null;
    }

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const selected = this.variantPicker.selectedOption;
      const selectedMedia =
        selected instanceof HTMLInputElement ? this.#resolveSwatchPreviewMedia(selected) : null;
      const id = selectedMedia?.id || selected.dataset.optionMediaId;
      const url = selectedMedia?.url || selected.closest('label')?.dataset?.optionMediaUrl;

      if (id) {
        const slide = slideshow.refs.slides?.find(
          (s) => s instanceof HTMLElement && String(s.getAttribute('slide-id')) === String(id)
        );
        if (slide) {
          slideshow.select({ id }, undefined, { animate: false });
          return;
        }
      }

      if (url) {
        this.#setGalleryImageUrl(url);
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));

    // Initialize swatch label on load
    this.#updateSwatchLabel();
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Updates the visible swatch name label under the swatches, if present.
   */
  #updateSwatchLabel() {
    const labelEl = this.querySelector('.variant-swatches__label');
    if (!labelEl) return;

    const checkedInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name*="-swatch"]:checked'));
    const swatchLabel = checkedInput?.dataset?.swatchLabel;

    if (swatchLabel) {
      labelEl.textContent = swatchLabel;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = /** @type {HTMLInputElement} */ (event.target);
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      if (this.dataset.disableSwatchesChange === 'true') {
        const productUrl = this.dataset.productUrl?.split('?')[0];
        if (productUrl) {
          event.stopPropagation();
          const url = new URL(productUrl, window.location.origin);
          url.searchParams.set('variant', firstAvailableVariantId);
          window.location.assign(url.toString());
        }
        return;
      }

      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);

      // Update label to reflect the newly selected swatch
      this.#updateSwatchLabel();
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);

    // After the default handling, update the label as well
    this.#updateSwatchLabel();
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
